import { AuthenticationResponseJSON, RegistrationResponseJSON, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { action, observable } from 'mobx';
import API, { FetchStatus, PasskeyErrorType } from '../util/ApiUtil';
import CookieApi from '../util/CookieApi';
import ErrorUtil from '../util/ErrorUtil';
import { getUrlForComponent } from '../util/NavigationUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import UiStore from './UiStore';

export const PASSKEY_EXISTS_COOKIE_NAME = 'passkeyExists';

export interface Passkey {
  credentialId: string;
  creationDate: string;
  credentialName: string;
}

export default class PasskeyStore {
  @observable accessor passkeys: Passkey[] = [];
  @observable accessor status: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor passkeysStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor deleteStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor errorType: PasskeyErrorType | null = null;
  @observable accessor offerRegisterModalVisible: boolean = false;
  @observable accessor offerRegisterModalOnComplete: () => void = () => {};
  @observable accessor wasPasskeyRegisterSuccessful: boolean | null = null;

  private routeStore: RouteStore;
  private uiStore: UiStore;

  constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.uiStore = rootStore.uiStore;
  }

  @action.bound
  public async authenticate() {
    try {
      this.status = FetchStatus.FETCHING;
      const loginOptions = await API.get('/webauthn/login/options');
      const publicKey = loginOptions.data.params.publicKey;
      const authResponse = await startAuthentication({ optionsJSON: publicKey });
      const verifyResponse = await API.post(
        `/webauthn/login/verify${this.routeStore.formForwardParams ?? ''}`,
        this.mapAssertionResponse(authResponse, publicKey.authSessionId),
      );
      this.status = FetchStatus.FETCHED;
      this.setPasskeyExists(true);
      const { redirectUrl, component } = verifyResponse.data;
      if (component) {
        this.routeStore.route(getUrlForComponent(verifyResponse.data, this.routeStore.formForwardParams));
      } else if (redirectUrl) {
        this.routeStore.route(redirectUrl);
      }
    } catch (e: Error | string | any) {
      if (e.name === 'NotAllowedError') {
        // User cancelled the authentication or it timed out
        this.status = FetchStatus.ERROR;
        this.errorType = PasskeyErrorType.USER_CANCELLED;
        return;
      }
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.status = FetchStatus.ERROR;
      this.errorType = PasskeyErrorType.GENERAL;
    }
  }

  @action.bound
  public async register() {
    this.uiStore.hideNotices();
    try {
      this.status = FetchStatus.FETCHING;
      const response = await API.get('/webauthn/register/options');
      const attestationResponse = await startRegistration({ optionsJSON: response.data.params.publicKey });
      await API.post('/webauthn/register', this.mapAttestationResponse(attestationResponse));
      this.status = FetchStatus.FETCHED;
      this.wasPasskeyRegisterSuccessful = true;
      this.setPasskeyExists(true);
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.status = FetchStatus.ERROR;
      this.wasPasskeyRegisterSuccessful = false;
    }
  }

  @action.bound
  public async fetchPasskeys() {
    try {
      this.passkeysStatus = FetchStatus.FETCHING;
      const response = await API.get('/webauthn/list');
      this.passkeys = response.data.params.passkeys;
      this.passkeysStatus = FetchStatus.FETCHED;
      this.setPasskeyExists(response.data.params.passkeys.length > 0);
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.passkeysStatus = FetchStatus.ERROR;
    }
  }

  @action.bound
  public async deleteKey(id: string) {
    this.uiStore.hideNotices();
    try {
      this.deleteStatus = FetchStatus.FETCHING;
      await API.delete(`/webauthn/delete?credentialId=${id}`);
      this.deleteStatus = FetchStatus.FETCHED;
      this.passkeys = this.passkeys.filter((key) => key.credentialId !== id);
      this.setPasskeyExists(this.passkeys.length > 0);
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      this.deleteStatus = FetchStatus.ERROR;
    }
  }

  public async skipPasskeyOfferRegister() {
    try {
      await API.post('/webauthn/skip-register');
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
    }
  }

  @action.bound
  public async handleOfferRegisterModalLogic(onRegisterComplete: () => void, hasInitiatedPasskeyRegister = false) {
    if (!this.uiStore.isMobileDevice || !this.uiStore.isPasskeySupported) {
      onRegisterComplete();
      return;
    }

    try {
      if (hasInitiatedPasskeyRegister) {
        this.offerRegisterModalVisible = true;
      } else {
        const response = await API.get('/webauthn/offer-register');
        this.offerRegisterModalVisible = response.data.params.offerRegister;
      }

      if (!this.offerRegisterModalVisible) {
        onRegisterComplete();
        return;
      }

      this.offerRegisterModalOnComplete = () => {
        this.offerRegisterModalVisible = false;
        this.wasPasskeyRegisterSuccessful = null;
        onRegisterComplete();
      };
    } catch (e: Error | string | any) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e);
      onRegisterComplete();
    }
  }

  public handleAddNewPasskeyOnAccountPage = async () => {
    await this.register();
    if (this.status === FetchStatus.FETCHED) {
      this.uiStore.showSuccessNotice('passkey.register.success');
      await this.fetchPasskeys();
    } else if (this.status === FetchStatus.ERROR) {
      this.uiStore.showErrorNotice('passkey.register.error');
    }
  };

  public handleDeletePasskeyOnAccountPage = async (id: string) => {
    await this.deleteKey(id);
    if (this.deleteStatus === FetchStatus.FETCHED) {
      this.uiStore.showSuccessNotice('passkey.delete.success');
    } else if (this.deleteStatus === FetchStatus.ERROR) {
      this.uiStore.showErrorNotice('passkey.delete.error');
    }
  };

  public setPasskeyExists = (exists: boolean) => {
    CookieApi.setCookie(PASSKEY_EXISTS_COOKIE_NAME, String(exists));
  };

  public getPasskeyExists = () => CookieApi.getCookieValue(PASSKEY_EXISTS_COOKIE_NAME) === 'true';

  private mapAssertionResponse(
    { id, rawId, type, response: { authenticatorData, clientDataJSON, signature, userHandle } }: AuthenticationResponseJSON,
    authSessionId: string,
  ) {
    return {
      id,
      rawId,
      type,
      authSessionId,
      response: {
        authenticatorData,
        clientDataJson: clientDataJSON,
        signature,
        userHandle,
      },
    };
  }

  private mapAttestationResponse({ id, rawId, type, response: { clientDataJSON, attestationObject } }: RegistrationResponseJSON) {
    return {
      id,
      rawId,
      type,
      response: {
        clientDataJSON,
        attestationObject,
      },
    };
  }
}
